import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { X, Send, Check, User } from 'lucide-react';

export function ShareQuoteModal({ api, leadId, quoteId, onClose }) {
  const [staffUsers, setStaffUsers] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [customEmail, setCustomEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/api/users').then(res => {
      setStaffUsers((res.data || []).filter(u => u.email));
    }).catch(() => {});
  }, []);

  const toggleEmail = (email) => {
    setSelectedEmails(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };

  const addCustom = () => {
    const e = customEmail.trim();
    if (e && e.includes('@') && !selectedEmails.includes(e)) {
      setSelectedEmails([...selectedEmails, e]);
      setCustomEmail('');
    }
  };

  const handleSend = async () => {
    if (selectedEmails.length === 0) { toast.error('Select at least one recipient'); return; }
    setSending(true);
    try {
      const res = await api.post(`/api/leads/${leadId}/quotes/${quoteId}/share`, {
        staff_emails: selectedEmails,
        message
      });
      toast.success(`Quote shared with ${res.data.sent} staff member${res.data.sent > 1 ? 's' : ''}`);
      onClose();
    } catch {
      toast.error('Failed to share');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#0A1628]">Share Quote</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">Send a read-only preview link to staff members. Only the client can sign and pay.</p>

        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Team Members</p>
          <div className="max-h-48 overflow-y-auto space-y-1 border rounded-xl p-2">
            {staffUsers.map(u => (
              <StaffRow key={u.id} user={u} selected={selectedEmails.includes(u.email)} onToggle={() => toggleEmail(u.email)} />
            ))}
            {staffUsers.length === 0 && <p className="text-sm text-gray-400 text-center py-3">No staff found</p>}
          </div>
        </div>

        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Or add email</p>
          <div className="flex gap-2">
            <input type="email" value={customEmail} onChange={(e) => setCustomEmail(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }} placeholder="email@company.com" className="flex-1 px-3 py-2 border rounded-lg text-sm" />
            <Button variant="outline" size="sm" onClick={addCustom}>Add</Button>
          </div>
        </div>

        {selectedEmails.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Sending to ({selectedEmails.length})</p>
            <div className="flex flex-wrap gap-1.5">
              {selectedEmails.map(e => (
                <span key={e} className="inline-flex items-center gap-1 px-2 py-1 bg-[#014DB7]/10 text-[#014DB7] rounded-lg text-xs">
                  {e}
                  <button onClick={() => toggleEmail(e)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Message (optional)</p>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Please review this quote before we send to the client..." rows={2} className="w-full px-3 py-2 border rounded-lg text-sm resize-none" />
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSend} disabled={sending || selectedEmails.length === 0} className="bg-[#014DB7]">
            <Send className="w-4 h-4 mr-2" /> {sending ? 'Sending...' : `Share with ${selectedEmails.length}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function StaffRow({ user, selected, onToggle }) {
  return (
    <button onClick={onToggle} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${selected ? 'bg-[#014DB7]/10 border border-[#014DB7]/30' : 'hover:bg-gray-50'}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${selected ? 'bg-[#014DB7] text-white' : 'bg-gray-200 text-gray-500'}`}>
        {selected ? <Check className="w-4 h-4" /> : <User className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#0A1628] truncate">{user.first_name} {user.last_name}</p>
        <p className="text-xs text-gray-500 truncate">{user.email}</p>
      </div>
    </button>
  );
}
